import React, { useState, useEffect, useMemo } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { canAccessRoute } from "@/lib/roles";
import { api, fmtFecha } from "@/lib/api";
import {
  LayoutDashboard, Building2, Users, Clock, FileText, MinusCircle, PlusCircle,
  CalendarRange, Palmtree, Gavel, FileSignature, LogOut, Heart, ChevronDown, Menu, X, Coins, Receipt, ListChecks, UserCog, ShieldCheck, FileBarChart2, CreditCard, AlertTriangle, ArrowLeftRight, Check
} from "lucide-react";

// 2 filas equilibradas para que nada se oculte en "Más"
const NAV_ROW1 = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/empresa", label: "Empresa", icon: Building2 },
  { to: "/empleados", label: "Empleados", icon: Users },
  { to: "/asistencia", label: "Asistencia", icon: Clock },
  { to: "/incapacidades", label: "Incapacidades", icon: Heart },
  { to: "/rebajos", label: "Rebajos", icon: MinusCircle },
  { to: "/bonificaciones", label: "Bonificaciones", icon: PlusCircle },
  { to: "/solicitudes", label: "Solicitudes", icon: ListChecks },
  { to: "/reportes", label: "Reportes", icon: FileBarChart2 },
];
const NAV_ROW2 = [
  { to: "/planillas", label: "Planillas", icon: FileText },
  { to: "/planillas-mensuales", label: "Planillas Mensuales", icon: CalendarRange },
  { to: "/vacaciones", label: "Vacaciones", icon: Palmtree },
  { to: "/aguinaldo", label: "Aguinaldo", icon: Coins },
  { to: "/documentacion", label: "Documentación", icon: Gavel },
  { to: "/acciones-personal", label: "Acciones de Personal", icon: UserCog },
  { to: "/liquidaciones", label: "Liquidaciones", icon: Receipt },
  { to: "/contratos", label: "Contratos", icon: FileSignature },
];

const NavButton = ({ n }) => {
  const Icon = n.icon;
  if (n.disabled) {
    return (
      <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-[15px] font-medium text-secondary-c opacity-50 cursor-not-allowed select-none">
        <Icon size={17} />
        <span>{n.label}</span>
        <span className="text-[9px] uppercase tracking-wider opacity-80 ml-0.5">Pronto</span>
      </div>
    );
  }
  return (
    <NavLink to={n.to}
      className={({ isActive }) => `flex items-center gap-2 px-4 py-2.5 rounded-xl text-[15px] font-medium transition-all whitespace-nowrap ${isActive ? "bg-[#274735] text-white shadow-sm" : "text-secondary-c hover:bg-[#FAFAF7] hover:text-primary-c"}`}
      data-testid={`nav-${n.label.replace(/[\s.]/g, "-").toLowerCase()}`}>
      <Icon size={17} />
      <span>{n.label}</span>
    </NavLink>
  );
};

export default function Layout({ children }) {
  const { user, logout, setUser } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [userMenu, setUserMenu] = useState(false);
  // Multi-tenant switcher: cuentas del email actual
  const [myAccounts, setMyAccounts] = useState([]);
  const [showSwitcher, setShowSwitcher] = useState(false);
  const [switchingTo, setSwitchingTo] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/auth/my-accounts").then(r => {
      setMyAccounts(r.data?.accounts || []);
    }).catch(() => setMyAccounts([]));
  }, [user?.user_id]);

  const otherAccounts = myAccounts.filter(a => !a.is_current);

  const switchTo = async (empresa_id) => {
    setSwitchingTo(empresa_id);
    try {
      const { data } = await api.post("/auth/switch-account", { empresa_id });
      if (data?.token) localStorage.setItem("np_token", data.token);
      setUser(data);
      setShowSwitcher(false);
      setUserMenu(false);
      // Redirigir al dashboard de la nueva empresa (recarga liviana para
      // asegurar que TODOS los useMemo cachados del tenant anterior se limpien).
      window.location.href = "/dashboard";
    } catch (e) {
      alert("No se pudo cambiar de empresa: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSwitchingTo(null);
    }
  };
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [empresa, setEmpresa] = useState(null);

  const role = user?.role;
  const isSuperAdmin = user?.is_super_admin || user?.role === "super_admin";
  const empresaSuspendida = !!user?.empresa_suspendida;
  const row1 = empresaSuspendida ? [] : NAV_ROW1.filter(n => canAccessRoute(role, n.to));
  const row2 = empresaSuspendida ? [] : NAV_ROW2.filter(n => canAccessRoute(role, n.to));

  // Si la empresa está suspendida y el usuario intenta acceder a algo distinto
  // de /pagos, lo redirigimos al módulo de pagos.
  useEffect(() => {
    if (empresaSuspendida && location.pathname !== "/pagos") {
      nav("/pagos", { replace: true });
    }
  }, [empresaSuspendida, location.pathname, nav]);

  // Cargar datos de la empresa (para detectar auto-upgrade de plan)
  useEffect(() => {
    if (!user || isSuperAdmin) return;
    api.get("/empresa").then(r => setEmpresa(r.data)).catch(() => {});
  }, [user, isSuperAdmin]);

  const ackUpgrade = async () => {
    try {
      await api.put("/empresa/ack-upgrade");
      setEmpresa(e => e ? { ...e, plan_upgraded_from: null, plan_upgraded_at: null, plan_downgraded_from: null, plan_downgraded_at: null } : e);
    } catch (err) { console.error("ack-upgrade failed", err); }
  };
  const upgradedFrom = empresa?.plan_upgraded_from;
  const upgradedTo = empresa?.plan;
  const downgradedFrom = empresa?.plan_downgraded_from;
  const downgradedTo = empresa?.plan;
  const nombrePlan = (k) => ({plata:"Plata", oro:"Oro", platino:"Platino", diamante:"Diamante", master:"Master"}[k] || k);

  // === Recordatorio de pago próximo ===
  // Se muestra un modal si faltan ≤3 días para la fecha de próximo pago.
  // Si `proximo_pago_at` no existe, usa el fallback (fecha de registro + 25 días).
  // Se puede posponer con "Recordar más tarde" (guardado en sessionStorage
  // por empresa+fecha, para que reaparezca en el próximo login).
  const proximoPagoISO = useMemo(() => {
    if (empresa?.proximo_pago_at) return empresa.proximo_pago_at;
    // Fallback: fecha de registro (created_at) + 25 días.
    const base = empresa?.created_at ? new Date(empresa.created_at) : new Date();
    return new Date(base.getTime() + 25 * 24 * 60 * 60 * 1000).toISOString();
  }, [empresa?.proximo_pago_at, empresa?.created_at]);

  const [showPagoModal, setShowPagoModal] = useState(false);
  useEffect(() => {
    if (!empresa || empresaSuspendida || isSuperAdmin || empresa.es_gratuita) return;
    const proximo = new Date(proximoPagoISO);
    if (isNaN(proximo.getTime())) return;
    const hoy = new Date();
    const dias = Math.ceil((proximo - hoy) / (1000 * 60 * 60 * 24));
    if (dias > 3 || dias < 0) return;
    // Ya en /pagos no tiene sentido mostrar el aviso
    if (location.pathname === "/pagos") return;
    const key = `pago-reminder-dismiss:${empresa.id || empresa.empresa_id || "self"}:${proximoPagoISO.slice(0,10)}`;
    if (sessionStorage.getItem(key) === "1") return;
    setShowPagoModal(true);
  }, [empresa, empresaSuspendida, isSuperAdmin, location.pathname, proximoPagoISO]);

  const proximoPagoDias = useMemo(() => {
    const p = new Date(proximoPagoISO);
    if (isNaN(p.getTime())) return null;
    return Math.ceil((p - new Date()) / (1000 * 60 * 60 * 24));
  }, [proximoPagoISO]);

  const proximoPagoLabel = proximoPagoISO
    ? new Date(proximoPagoISO).toLocaleDateString("es-CR", { day: "2-digit", month: "long", year: "numeric" })
    : "";

  const dismissPagoModal = (recordarLuego) => {
    if (recordarLuego && proximoPagoISO) {
      const key = `pago-reminder-dismiss:${empresa?.id || empresa?.empresa_id || "self"}:${proximoPagoISO.slice(0,10)}`;
      sessionStorage.setItem(key, "1");
    }
    setShowPagoModal(false);
  };
  const irAPagos = () => {
    dismissPagoModal(true);
    nav("/pagos");
  };

  useEffect(() => {
    if (!isSuperAdmin) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/admin/pending-count");
        if (!cancelled) setPendingCount(data?.count || 0);
      } catch { /* ignore */ }
    };
    tick();
    const t = setInterval(tick, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, [isSuperAdmin]);

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="min-h-screen bg-app flex flex-col">
      {/* TOP NAV */}
      <header className="bg-surface border-b border-c sticky top-0 z-40">
        <div className="max-w-[1600px] mx-auto px-4 md:px-6">

          {/* Top bar: Brand + User */}
          <div className="flex items-center justify-between py-3.5">
            <div className="flex items-center gap-3.5">
              <img src="/vertice-logo.png" alt="Vértice" className="h-14 w-auto" />
              <div className="leading-tight">
                <div className="text-xs md:text-[13px] text-secondary-c font-medium uppercase tracking-widest">Sistema de Recursos Humanos</div>
              </div>
            </div>

            <button className="lg:hidden np-btn np-btn-ghost p-2" onClick={() => setMobileOpen(v => !v)} data-testid="mobile-nav-toggle">
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>

            <div className="hidden lg:block relative">
              <button onClick={() => setUserMenu(v => !v)} className="flex items-center gap-3 px-2.5 py-1.5 rounded-xl hover:bg-[#FAFAF7] transition-colors" data-testid="user-menu-trigger">
                <div className="w-11 h-11 rounded-full bg-primary-c text-white flex items-center justify-center font-semibold text-sm">
                  {user?.name?.split(" ").slice(0, 2).map(s => s[0]?.toUpperCase()).join("")}
                </div>
                <div className="hidden xl:block leading-tight text-left">
                  <div className="text-sm font-semibold truncate max-w-[180px]">{user?.name}</div>
                  <div className="text-xs text-secondary-c truncate max-w-[180px]">{user?.email}</div>
                </div>
                <ChevronDown size={15} className="text-secondary-c" />
              </button>
              {userMenu && (
                <div className="absolute right-0 top-full mt-1 bg-white rounded-xl border border-c shadow-lg py-1 min-w-[280px] z-50">
                  <div className="px-3 py-2.5 border-b border-c">
                    <div className="text-sm font-semibold truncate">{user?.name}</div>
                    <div className="text-xs text-secondary-c truncate">{user?.email}</div>
                    {myAccounts.find(a => a.is_current)?.empresa_nombre && (
                      <div className="text-[11px] text-primary-c mt-1 font-medium truncate">
                        {myAccounts.find(a => a.is_current)?.empresa_nombre}
                      </div>
                    )}
                  </div>
                  {otherAccounts.length > 0 && !showSwitcher && (
                    <button
                      onClick={() => setShowSwitcher(true)}
                      className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-primary-c hover:bg-[#FAFAF7]"
                      data-testid="switch-empresa-button"
                    >
                      <ArrowLeftRight size={16} /> Cambiar de empresa
                    </button>
                  )}
                  {showSwitcher && (
                    <div className="border-b border-c">
                      <div className="px-3 py-2 text-[11px] font-semibold text-secondary-c uppercase tracking-wide">
                        Tus otras cuentas
                      </div>
                      {otherAccounts.map(a => (
                        <button
                          key={a.user_id}
                          onClick={() => switchTo(a.empresa_id)}
                          disabled={!!switchingTo}
                          className="w-full flex items-start gap-2 px-3 py-2.5 text-sm hover:bg-[#FAFAF7] disabled:opacity-50 disabled:cursor-not-allowed text-left"
                          data-testid={`switch-empresa-option-${a.empresa_id}`}
                        >
                          <ArrowLeftRight size={14} className="mt-0.5 text-secondary-c flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-primary-c truncate">{a.empresa_nombre}</div>
                            <div className="text-[11px] text-secondary-c capitalize">
                              {a.role === "super_admin" ? "Super administrador" : a.role === "admin" ? "Administrador" : "Asistente"}
                              {a.is_owner ? " · Titular" : ""}
                            </div>
                          </div>
                          {switchingTo === a.empresa_id && <span className="text-[10px] text-secondary-c">Cambiando…</span>}
                        </button>
                      ))}
                      <button
                        onClick={() => setShowSwitcher(false)}
                        className="w-full px-3 py-1.5 text-[11px] text-secondary-c hover:text-primary-c text-left"
                        data-testid="switch-empresa-cancel"
                      >
                        ← Ocultar
                      </button>
                    </div>
                  )}
                  <button onClick={handleLogout} className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-error hover:bg-[#FDEDED]" data-testid="logout-button">
                    <LogOut size={16} /> Cerrar sesión
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* 2 filas de navegación (desktop) */}
          <nav className="hidden lg:block pb-3 pt-1 space-y-1.5">
            {empresaSuspendida ? (
              <div className="flex items-center gap-1.5">
                <NavLink
                  to="/pagos"
                  data-testid="nav-pagos-suspendida"
                  className={({ isActive }) => `flex items-center gap-2 px-4 py-2.5 rounded-xl text-[15px] font-medium transition-all whitespace-nowrap ${isActive ? "bg-[#274735] text-white shadow-sm" : "text-secondary-c hover:bg-[#FAFAF7] hover:text-primary-c"}`}
                >
                  <CreditCard size={17} />
                  <span>Suscripción</span>
                </NavLink>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {row1.map(n => <NavButton key={n.label} n={n} />)}
                </div>
                {row2.length > 0 && (
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {row2.map(n => <NavButton key={n.label} n={n} />)}
                    {isSuperAdmin && (
                      <NavLink
                        to="/admin/super-administrador"
                        data-testid="nav-admin-cuentas"
                        className={({ isActive }) => `flex items-center gap-2 px-4 py-2.5 rounded-xl text-[15px] font-medium transition-all whitespace-nowrap ${isActive ? "bg-[#274735] text-white shadow-sm" : "text-primary-color hover:bg-[#E6EFEA]"}`}
                      >
                        <ShieldCheck size={17} />
                        <span>Super Administrador</span>
                        {pendingCount > 0 && (
                          <span className="ml-1 min-w-[20px] h-[20px] px-1.5 rounded-full bg-accent-c text-white text-[11px] font-bold flex items-center justify-center" data-testid="nav-admin-badge">
                            {pendingCount}
                          </span>
                        )}
                      </NavLink>
                    )}
                    {!isSuperAdmin && (role === "admin") && !empresa?.es_gratuita && (
                      <NavLink
                        to="/pagos"
                        data-testid="nav-pagos"
                        className={({ isActive }) => `flex items-center gap-2 px-4 py-2.5 rounded-xl text-[15px] font-medium transition-all whitespace-nowrap ${isActive ? "bg-[#274735] text-white shadow-sm" : "text-secondary-c hover:bg-[#FAFAF7] hover:text-primary-c"}`}
                      >
                        <CreditCard size={17} />
                        <span>Suscripción</span>
                      </NavLink>
                    )}
                  </div>
                )}
              </>
            )}
          </nav>
        </div>

        {/* Mobile nav */}
        {mobileOpen && (
          <div className="lg:hidden border-t border-c py-2 px-4 space-y-1">
            {empresaSuspendida ? (
              <NavLink to="/pagos" onClick={() => setMobileOpen(false)}
                className={({ isActive }) => `flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium ${isActive ? "bg-[#274735] text-white" : "text-secondary-c"}`}>
                <CreditCard size={16} /> Suscripción
              </NavLink>
            ) : (
              [...row1, ...row2].map(n => {
                const Icon = n.icon;
                if (n.disabled) {
                  return (
                    <div key={n.label} className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium text-secondary-c opacity-50">
                      <Icon size={16} /> {n.label}
                      <span className="text-[9px] uppercase tracking-wider ml-auto">Pronto</span>
                    </div>
                  );
                }
                return (
                  <NavLink key={n.to} to={n.to} onClick={() => setMobileOpen(false)}
                    className={({ isActive }) => `flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium ${isActive ? "bg-[#274735] text-white" : "text-secondary-c"}`}>
                    <Icon size={16} /> {n.label}
                  </NavLink>
                );
              })
            )}
            <button onClick={handleLogout} className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-error">
              <LogOut size={16} /> Cerrar sesión
            </button>
          </div>
        )}
      </header>

      {/* Banner de suspensión */}
      {empresaSuspendida && (
        <div className="bg-[#FDEDED] border-b border-[#F0C6C6]" data-testid="empresa-suspendida-banner">
          <div className="max-w-[1800px] mx-auto px-4 md:px-6 lg:px-8 py-3 flex items-start gap-3">
            <AlertTriangle size={20} className="text-error flex-shrink-0 mt-0.5" />
            <div className="flex-1 text-sm">
              <div className="font-semibold text-error">Cuenta suspendida por falta de pago desde el día {fmtFecha(user?.empresa_suspension_at)}.</div>
              <div className="text-error/80 text-xs mt-0.5">
                Solo podés acceder al módulo de <strong>Suscripción</strong>. Adjuntá el comprobante de pago para regularizar tu cuenta; una vez validado, el super administrador reactivará tu acceso completo.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Banner de auto-upgrade de plan */}
      {upgradedFrom && upgradedTo && !empresaSuspendida && (
        <div className="bg-[#E6EFEA] border-b border-primary-c" data-testid="plan-upgraded-banner">
          <div className="max-w-[1800px] mx-auto px-4 md:px-6 lg:px-8 py-3 flex items-start gap-3">
            <CreditCard size={20} className="text-primary-c flex-shrink-0 mt-0.5" />
            <div className="flex-1 text-sm">
              <div className="font-semibold text-primary-c">
                Tu plan fue actualizado automáticamente: Plan {nombrePlan(upgradedFrom)} → Plan {nombrePlan(upgradedTo)}
              </div>
              <div className="text-xs text-secondary-c mt-0.5">
                Alcanzaste el límite de colaboradores de tu plan anterior. Tu suscripción se ajusta automáticamente para que sigas operando sin límites. El nuevo monto se refleja desde tu próximo cobro.
              </div>
            </div>
            <button onClick={ackUpgrade} className="np-btn np-btn-primary text-xs whitespace-nowrap" data-testid="plan-upgraded-ack">Entendido</button>
          </div>
        </div>
      )}

      {/* Banner de auto-downgrade de plan */}
      {downgradedFrom && downgradedTo && !upgradedFrom && !empresaSuspendida && (
        <div className="bg-[#FFF7E6] border-b border-yellow-300" data-testid="plan-downgraded-banner">
          <div className="max-w-[1800px] mx-auto px-4 md:px-6 lg:px-8 py-3 flex items-start gap-3">
            <CreditCard size={20} className="text-yellow-700 flex-shrink-0 mt-0.5" />
            <div className="flex-1 text-sm">
              <div className="font-semibold text-yellow-800">
                Tu plan fue ajustado automáticamente: Plan {nombrePlan(downgradedFrom)} → Plan {nombrePlan(downgradedTo)}
              </div>
              <div className="text-xs text-secondary-c mt-0.5">
                Se ajustó tu suscripción al plan más adecuado según tu cantidad actual de colaboradores activos. El nuevo monto se aplica desde tu próximo cobro.
              </div>
            </div>
            <button onClick={ackUpgrade} className="np-btn np-btn-primary text-xs whitespace-nowrap" data-testid="plan-downgraded-ack">Entendido</button>
          </div>
        </div>
      )}

      {/* Content */}
      <main className="flex-1 max-w-[1800px] mx-auto w-full p-4 md:p-6 lg:p-8 overflow-x-hidden">{children}</main>

      {/* Modal de recordatorio de pago próximo (≤2 días) */}
      {showPagoModal && (
        <div
          className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4 animate-in fade-in duration-200"
          data-testid="pago-reminder-modal"
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative">
            <button
              onClick={() => dismissPagoModal(false)}
              className="absolute top-3 right-3 text-secondary-c hover:text-primary-c p-1 rounded-lg hover:bg-gray-100"
              aria-label="Cerrar"
              data-testid="pago-reminder-close"
            >
              <X size={20} />
            </button>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#FFF7E6] flex items-center justify-center">
                <AlertTriangle size={24} className="text-yellow-700" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-primary-c mb-1">Pago próximo pendiente</h3>
                <p className="text-sm text-secondary-c leading-relaxed">
                  {proximoPagoDias === 0
                    ? <>Tu próximo pago vence <span className="font-semibold text-primary-c">hoy</span> ({proximoPagoLabel}).</>
                    : proximoPagoDias === 1
                    ? <>Tu próximo pago vence <span className="font-semibold text-primary-c">mañana</span> ({proximoPagoLabel}).</>
                    : <>Tu próximo pago vence en <span className="font-semibold text-primary-c">{proximoPagoDias} días</span> ({proximoPagoLabel}).</>
                  }
                </p>
                <p className="text-xs text-secondary-c mt-2">
                  Para evitar la suspensión de tu cuenta, ingresá al módulo de Pagos y subí el comprobante de tu pago.
                </p>
              </div>
            </div>
            <div className="mt-6 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
              <button
                onClick={() => dismissPagoModal(true)}
                className="np-btn np-btn-ghost text-sm"
                data-testid="pago-reminder-later"
              >
                Recordar más tarde
              </button>
              <button
                onClick={irAPagos}
                className="np-btn np-btn-primary text-sm inline-flex items-center gap-2 justify-center"
                data-testid="pago-reminder-go"
              >
                <CreditCard size={16} />
                Ir a Pagos
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
